// Where a redeemed invitation RESUMES (arc 101 slice 01 step 3).
//
// `lib/invitations.ts` owns the POST and the verdict, `lib/invite-copy.ts` owns
// which sentence a verdict reads as, and this owns the third thing that changes
// for its own reason: the screen the person is put on next. Same split, same
// reason, as `lib/task-dispatch.ts` / `lib/task-refusal.ts`.
//
// The landing is a WAYPOINT. Someone who followed a link out of their mail is
// mid-signup, and the place they belong is `/onboarding` — the funnel that boots
// to the next incomplete step off the account's onboarding state. Leaving them
// on a confirmation card whose only link is `/` makes a stranger's first screen
// of the product a dead end.
//
// Pure: no DOM, no router, no query. Every decision here is a function of a
// settled `InviteOutcome` plus the session's memberships, so it is decidable
// without either.

import type { InviteOutcome } from './invitations';
import { orgHref } from './org';
import type { MembershipView } from './session';

/** The funnel, as `orgHref` takes it — `ORG_ROUTE_TABLE`'s `onboarding` key. */
const ONBOARDING_PATH = '/onboarding';

/**
 * Whether a settled accept moves the person on, and into which account.
 *
 * A verdict rather than a nullable account id, because `null` alone would
 * collapse two unrelated answers: "this verdict is the end of the road" and
 * "you are in, and the invitation named no particular org". Both are reachable
 * and they send a person to two different places.
 */
export type InviteResume =
  | { readonly kind: 'stay' }
  | { readonly kind: 'resume'; readonly accountId: string | null };

/**
 * The two verdicts that say THIS identity holds access — `accepted` (it does
 * now) and `already_a_member` (it did already) — resume the funnel. Both carry
 * the account the access is in, which is exactly what the destination needs.
 *
 * Everything else stays, and each for its own reason. The four refusals have
 * nowhere to send anyone. `already_accepted` is the interesting one: it is an
 * `ok` tone and it still stays, because a redelivered click on a spent link
 * carries no account at all and its news IS the screen — "you used this on
 * <date>", or, when it was not you, a security event to report. Resuming it
 * would throw that away to land somebody in a funnel nobody asked them to open.
 */
export function inviteResume(outcome: InviteOutcome): InviteResume {
  return outcome.kind === 'accepted' || outcome.kind === 'already_a_member'
    ? { kind: 'resume', accountId: outcome.accountId }
    : { kind: 'stay' };
}

/**
 * The path a resume lands on. `memberships` is the session's list read AFTER the
 * accept: the row that names this account is the one the redemption just
 * created, so a list fetched before it cannot resolve the login.
 *
 * Three shapes, all reachable:
 *
 *  - a membership names the account → `/org/<login>/onboarding`, the URL every
 *    other org-scoped link in this app produces.
 *  - no membership names it → `/org/<account_id>/onboarding`. An id in that
 *    segment is a working address, not a half-broken fallback: the server
 *    resolves the `X-Dz-Account` ref by id FIRST and `github_login` second
 *    (`resolveAccountRef`, `apps/api/src/index.ts`), which is the same fallback
 *    `LegacyRedirect` already lands people on.
 *  - no account at all → the bare `/onboarding`. A PLATFORM invitation carries
 *    `accountId: null` by construction (`invitations_shape_ck`; it is the
 *    waitlist lift, not a membership grant), so the funnel it resumes is
 *    whichever org the session already holds — which is precisely what the
 *    top-level catch-all resolves a bare path to.
 */
export function inviteResumeHref(
  accountId: string | null,
  memberships: readonly MembershipView[],
): string {
  const login =
    accountId === null
      ? null
      : (memberships.find((m) => m.account_id === accountId)?.github_login ?? null);
  return orgHref(ONBOARDING_PATH, login ?? accountId);
}
